import { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const LegalContext = createContext(null);

export const LegalProvider = ({ children }) => {
  const { t } = useLanguage();
  const [type, setType] = useState(null); // "privacy" | "cookies" | null

  const open = useCallback((which) => setType(which), []);
  const close = useCallback(() => setType(null), []);

  const doc = type ? t.legal[type] : null;

  return (
    <LegalContext.Provider value={{ open, close }}>
      {children}
      <AnimatePresence>
        {doc && (
          <motion.div
            data-testid="legal-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          >
            <div className="absolute inset-0 bg-[#0B1F33]/70 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-[#E5E7EB] shadow-2xl"
            >
              <div className="sticky top-0 bg-[#0B1F33] text-white px-8 py-5 flex items-center justify-between">
                <h3 className="font-display text-xl font-bold tracking-tight">{doc.title}</h3>
                <button
                  data-testid="legal-close"
                  onClick={close}
                  className="grid place-items-center w-9 h-9 border border-white/20 hover:bg-[#B87333] transition-colors"
                  aria-label={t.legal.close}
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-8 py-8">
                <p className="text-xs uppercase tracking-widest text-[#9ca3af] mb-6">{t.legal.updated}</p>
                <div className="space-y-6">
                  {doc.body.map((s, i) => (
                    <div key={i}>
                      <h4 className="font-display font-bold text-[#0B1F33] text-base mb-1.5">{s.h}</h4>
                      <p className="text-[#4b5563] text-sm leading-relaxed">{s.p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LegalContext.Provider>
  );
};

export const useLegal = () => {
  const ctx = useContext(LegalContext);
  if (!ctx) throw new Error("useLegal must be used within LegalProvider");
  return ctx;
};
